import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field, CccisdInput, CccisdSelect } from 'cccisd-formik';
import axios from 'cccisd-axios';
import { client } from 'cccisd-apollo';
import Table from 'cccisd-table';
import Spinner from 'cccisd-spinner';
import notification from 'cccisd-notification';
import Loader from 'cccisd-loader';
import Cross from 'cccisd-icons/cross3';
import style from './style.css';
import appQuery from './appQuery.graphql';
import Accordion from '../../accordion.js';

const Boilerplate = window.cccisd.boilerplate;

export default class DevDeployForm extends React.Component {
    static propTypes = {
        closeModal: PropTypes.func,
    };

    modal = React.createRef();

    state = {
        apphandle: null,
        tags: [],
        parameters: [],
        loading: true,
        spinner: false,
        parametersCollapsed: true,
        apps: [],
    };

    componentDidMount = async () => {
        const response = await client.query({
            query: appQuery,
            fetchPolicy: 'network-only',
        });

        this.setAppData(response.data.groups.organizationList);

        this.setState({
            loading: false,
        });
    };

    // Prevent loss of unsaved changes
    confirmClose = () => {
        this.props.closeModal();
    };

    handleCollapse = id => {
        this.setState(prev => ({
            [id + 'Collapsed']: !prev[id + 'Collapsed'],
        }));
    };

    appSelect = async row => {
        this.setState({
            spinner: true,
        });
        const response = await axios
            .get(Boilerplate.route('api.monitoring.devops.app-info') + '?apphandle=' + row.handle + '&context=staging')
            .catch(() => {
                this.setState({ spinner: false });
            });

        this.setState({
            apphandle: row.handle,
            tags: response.data.data.tags,
            parameters: response.data.data.parameters,
            parametersCollapsed: true,
            spinner: false,
        });
    };

    handleContextChange = async selectedContext => {
        if (!this.state.apphandle || selectedContext === 'custom') return;
        this.setState({ spinner: true });
        const response = await axios
            .get(
                Boilerplate.route('api.monitoring.devops.app-info') +
                    '?apphandle=' +
                    this.state.apphandle +
                    '&context=' +
                    selectedContext
            )
            .catch(() => {
                this.setState({ spinner: false });
            });
        if (response) {
            this.setState({
                parameters: response.data.data.parameters,
                parametersCollapsed: true,
                spinner: false,
            });
        }
    };

    unselectApp = () => {
        this.setState({
            apphandle: null,
        });
    };

    setAppData = orgs => {
        let apps = [];

        orgs.forEach(org =>
            apps.push({
                id: org.group.groupId,
                label: org.group.label,
                handle: org.fields.git_handle,
                settings: org.group.settings,
            })
        );

        this.setState({
            apps,
        });
    };

    getAppInfo = () => {
        let info = {};

        this.state.apps.forEach(app => {
            if (app.handle === this.state.apphandle) {
                info = app;
            }
        });

        return info;
    };

    getTagList = () => {
        let taglist = [{ value: null, label: 'Choose build tag...' }];
        this.state.tags.forEach(tag => {
            let label = tag;
            if (taglist.length === 1) {
                label = tag + ' (latest)';
            }
            taglist.push({
                value: tag,
                label,
            });
        });

        return taglist;
    };

    getEnvironments = () => {
        const app = this.getAppInfo();

        let envs = [];

        if (
            app.settings.environments &&
            app.settings.environments.staging &&
            app.settings.environments.staging.current_version
        ) {
            envs.push({
                value: 'staging',
                label: 'Staging (current build: ' + app.settings.environments.staging.current_version + ')',
            });
        } else {
            envs.push({
                value: 'staging',
                label: 'Staging (no current staging build)',
            });
        }

        if (app.settings.environments && app.settings.environments.qa && app.settings.environments.qa.current_version) {
            envs.push({
                value: 'qa',
                label: 'QA (current build: ' + app.settings.environments.qa.current_version + ')',
            });
        }

        const knownEnvs = ['prod', 'production', 'staging', 'qa', 'codebuild', 'wordpress'];
        if (app.settings && app.settings.environments) {
            Object.keys(app.settings.environments).forEach(envKey => {
                if (!knownEnvs.includes(envKey)) {
                    const envData = app.settings.environments[envKey];
                    const version = envData && envData.current_version;
                    envs.push({
                        value: envKey,
                        label: version ? `${envKey} (current build: ${version})` : `${envKey} (no current build)`,
                    });
                }
            });
        }

        envs.push({
            value: 'custom',
            label: 'Custom...',
        });

        return envs;
    };

    onSubmit = async (values, actions) => {
        this.setState({
            loading: true,
        });

        let formattedData = values;
        formattedData.apphandle = this.state.apphandle;

        if (values.custom) {
            formattedData.contexts.push(values.customContext);
        }
        const response = await axios
            .post(Boilerplate.route('api.monitoring.devops.deploy.dev'), { ...formattedData })
            .catch(() => {
                this.setState({ loading: false });
            });

        this.setState({
            loading: false,
        });

        if (!response || response.data.status !== 'success') {
            notification({ message: 'An error occured', type: 'danger' });
        }

        this.props.closeModal();
    };

    validate = values => {
        const errors = {};

        if (!this.state.apphandle) {
            notification({
                type: 'warning',
                message: 'Application is required.',
            });
            errors.tag = 'Application is required.';
            return errors;
        }
        if (!values.tag || values.tag === 'Choose build tag...') {
            errors.tag = 'Build tag is required';
        }
        if (values.context === 'custom' && !values.customContext) {
            errors.customContext = 'Custom context is required if selected';
        }
        const reserved = ['staging', 'qa', 'production', 'codebuild'];
        if (values.context === 'custom' && reserved.includes(values.customContext)) {
            errors.customContext = `"${values.customContext}" is a reserved environment name`;
        }

        return errors;
    };

    appColumns = [
        { name: 'handle', label: 'Handle', filter: true, sort: true },
        { name: 'label', label: 'Name', filter: true, sort: true },
    ];

    setRowClass = row => {
        if (row.handle === this.state.apphandle) {
            return style.highlighted;
        }

        return style.gray;
    };

    showSubscribedPeople = () => {
        const names = [
            'Alice Johnson',
            'Bob Miller',
            'Charlie Nguyen',
            'Diana Lopez',
            'Ethan Wright',
            'Fiona Patel',
            'Gabriel Chen',
            'Hannah Thompson',
            'Isaac Rodriguez',
            'Jasmine Park',
        ];

        if (names.length === 0) {
            return (
                <div style={{ marginTop: '2em', fontStyle: 'italic', color: '#555' }}>
                    0 staff subscribed to notifications
                </div>
            );
        }

        return (
            <Accordion
                title={`Subscribed People (${names.length})`}
                id="subscribedPeople"
                handleCollapse={this.handleCollapse}
                collapsed={this.state.subscribedPeopleCollapsed}
            >
                <ul style={{ listStyle: 'disc', paddingLeft: '1.5em', marginTop: '0.5em' }}>
                    {names.map(name => (
                        <li key={name}>{name}</li>
                    ))}
                </ul>
            </Accordion>
        );
    };

    renderParametersTable = () => {
        const parameterList = Object.entries(this.state.parameters);

        return (
            <table>
                <tbody>
                    {parameterList.map(value => (
                        <tr key={value[0]}>
                            <td>{value[0]}</td>
                            <td>{value[1]}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    render() {
        const { loading, apphandle, spinner, parameters } = this.state;

        const initialValues = {
            context: 'staging',
        };

        let envMethod = 'SHARED';
        let envWebserver = 'shareddev1';
        let envQueueserver = 'queuedev1';

        const app = this.getAppInfo();

        if (app && app.settings) {
            if (parameters) {
                if (parameters.DEPLOY_APP_TARGET) {
                    if (parameters.DEPLOY_APP_TARGET === 'FARGATE') {
                        envMethod = 'FARGATE';
                        envWebserver = '';
                    } else {
                        envMethod = 'SHARED';
                        envWebserver = parameters.DEPLOY_APP_TARGET;
                    }
                }
                if (parameters.DEPLOY_QUEUE_TARGET) {
                    envQueueserver = parameters.DEPLOY_QUEUE_TARGET;
                }
            }
        }

        if (loading) {
            return <Loader loading />;
        }

        return (
            <>
                <div className={style.replaceClose}>
                    <button className="close" type="button" onClick={this.confirmClose}>
                        <Cross />
                    </button>
                </div>
                <Formik
                    key={apphandle}
                    initialValues={initialValues}
                    validate={this.validate}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={this.onSubmit}
                    render={({ values, errors }) => {
                        return (
                            <Form>
                                {!apphandle && (
                                    <>
                                        {!spinner && (
                                            <div className={style.selectApp}>
                                                <h4>Select Application to Deploy</h4>
                                                <Table
                                                    columns={this.appColumns}
                                                    data={this.state.apps}
                                                    onRowClick={this.appSelect}
                                                    setRowClass={this.setRowClass}
                                                    perPage={10}
                                                    showPerPageOptions={false}
                                                    showRowClickable
                                                />
                                            </div>
                                        )}
                                        {spinner && (
                                            <div style={{ minHeight: '270px', position: 'relative' }}>
                                                <Spinner options={{ width: 4, radius: 15, height: 4 }} />
                                            </div>
                                        )}
                                    </>
                                )}
                                {apphandle && (
                                    <div style={{ minHeight: '270px' }}>
                                        <h4>
                                            {app.label} / {app.handle}{' '}
                                            <span onClick={this.unselectApp} className={style.unselect}>
                                                <Cross />
                                            </span>
                                        </h4>
                                        <div>
                                            <b>Tag:</b>
                                            <Field name="tag" component={CccisdSelect} options={this.getTagList()} />
                                        </div>
                                        <div>
                                            <b>Environment:</b>
                                            <div>
                                                <Field name="context">
                                                    {({ field, form }) => (
                                                        <CccisdSelect
                                                            field={{
                                                                ...field,
                                                                onChange: e => {
                                                                    field.onChange(e);
                                                                    this.handleContextChange(e.target.value);
                                                                },
                                                            }}
                                                            form={form}
                                                            options={this.getEnvironments()}
                                                        />
                                                    )}
                                                </Field>
                                            </div>
                                            {values.context === 'custom' && (
                                                <div className={style.custom_env}>
                                                    <Field name="customContext" component={CccisdInput} />
                                                </div>
                                            )}
                                        </div>
                                        {values.context !== 'custom' && values.context !== 'production' && (
                                            <>
                                                <b>{values.context} Settings</b>
                                                <table className={style.settings_table}>
                                                    <tbody>
                                                        <tr>
                                                            <td className={style.settings_key}>Deployment method</td>
                                                            <td className={style.settings_value}>{envMethod}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className={style.settings_key}>Web server</td>
                                                            <td className={style.settings_value}>{envWebserver}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className={style.settings_key}>Queue server</td>
                                                            <td className={style.settings_value}>{envQueueserver}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                <Accordion
                                                    title={`${values.context} Parameters`}
                                                    id="parameters"
                                                    handleCollapse={this.handleCollapse}
                                                    collapsed={this.state.parametersCollapsed}
                                                >
                                                    {this.renderParametersTable()}
                                                </Accordion>
                                                {this.showSubscribedPeople()}
                                            </>
                                        )}
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginTop: '1em',
                                    }}
                                >
                                    <button className="btn btn-primary" type="button" onClick={this.confirmClose}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        type="submit"
                                        style={{
                                            backgroundColor: '#F0AD4E',
                                            borderColor: '#fff',
                                        }}
                                    >
                                        Deploy
                                    </button>
                                </div>
                            </Form>
                        );
                    }}
                />
            </>
        );
    }
}
